import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Avatar, crmToast, CrmToaster } from './crmUi'
import { Button } from '../shared/Button'
import {
  Contract, CtPriceBook, CtSelections, ctByLead, ctCountersign, ctGet, ctLink, ctPatch, ctRotate, ctSetStart, ctStatus,
} from './crmApi'
import { ctCompute, moneyIn } from './contractsCalc'
import { SOW_FILLS } from './contractContent'

/**
 * CONTRACT STUDIO (Lee, 6 Oct 2026, Phase 1) — /crm/contracts/:leadId.
 * NOT the usual lead page: build the lead's agreement (parties, plan &
 * pricing with per-contract overrides, which choices the customer gets,
 * professional services), preview it live — including the 12/24/36 ×
 * monthly/annual discount matrix priced with THIS basket — and copy the
 * customer link. Signing lands in Phase 3 (endpoints are live already).
 */

const inputCls = 'rounded-lg border border-border bg-surface px-2.5 py-1.5 text-[12px] text-txt-primary outline-none focus:border-brand-400'
/** Titled sub-panel — the tidy grouping Lee asked for (28 Oct 2026). */
const SubCard: React.FC<{ title: string; hint?: string; children?: React.ReactNode }> = ({ title, hint, children }) => (
  <div className="rounded-lg border border-border/70 bg-surface-secondary/50 px-3 py-2 mt-2">
    <p className="text-[9.5px] font-extrabold uppercase tracking-[0.08em] text-txt-tertiary mb-1">
      {title}{hint && <span className="ml-1.5 font-semibold normal-case tracking-normal opacity-80">· {hint}</span>}
    </p>
    {children}
  </div>
)
/** One label + right-aligned price input row. */
const PriceRow: React.FC<{ label: React.ReactNode; tbc?: boolean; value: number; locked: boolean; onChange: (n: number) => void }> = ({ label, tbc, value, locked, onChange }) => (
  <div className="flex items-center gap-2 py-1 border-b border-border/40 last:border-0">
    <span className="text-[12px] text-txt-primary flex-1 min-w-0">{label}{tbc && <span className="text-[9px] text-amber-600 font-bold"> · TBC</span>}</span>
    <input type="number" value={value} disabled={locked} onChange={(e) => onChange(Number(e.target.value) || 0)} className={`${inputCls} w-24 text-right shrink-0`} />
  </div>
)
const cardCls = 'rounded-xl border border-border bg-surface p-4 shadow-sm'
/** One section row: Fixed (default, we set the value) ⇄ They choose. */
const FixRow: React.FC<{ label: string; choice: boolean; locked: boolean; onMode: (choice: boolean) => void; children?: React.ReactNode }> = ({ label, choice, locked, onMode, children }) => (
  <div className="py-2.5 border-t border-border/70 first:border-t-0 first:pt-1">
    <div className="flex items-center gap-2">
      <span className="text-[12px] font-bold text-txt-primary flex-1">{label}</span>
      <span className="inline-flex rounded-full border border-border overflow-hidden">
        <button disabled={locked} onClick={() => onMode(false)} className={`px-2.5 py-1 text-[10px] font-bold ${!choice ? 'bg-brand-600 text-white' : 'bg-surface text-txt-tertiary hover:bg-surface-hover'}`}>FIXED BY US</button>
        <button disabled={locked} onClick={() => onMode(true)} className={`px-2.5 py-1 text-[10px] font-bold ${choice ? 'bg-brand-600 text-white' : 'bg-surface text-txt-tertiary hover:bg-surface-hover'}`}>THEY CHOOSE</button>
      </span>
    </div>
    {children && <div className="mt-1.5">{children}</div>}
  </div>
)

const CT_BADGE: Record<string, string> = {
  draft: 'bg-surface-tertiary text-txt-secondary border-border',
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  signed: 'bg-amber-50 text-amber-700 border-amber-300',
  countersigned: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  void: 'bg-red-50 text-red-600 border-red-200',
}

export const ContractStudio: React.FC = () => {
  const { leadId = '' } = useParams()
  const [searchParams] = useSearchParams()
  const contractId = searchParams.get('c') // open a SPECIFIC contract (Lee, 14 Oct 2026)
  const navigate = useNavigate()
  const [ct, setCt] = useState<Contract | null>(null)
  const [book, setBook] = useState<CtPriceBook | null>(null)
  const [sel, setSel] = useState<CtSelections | null>(null)
  const [pricingDirty, setPricingDirty] = useState(false)
  const [linkOpen, setLinkOpen] = useState(false)
  const [link, setLink] = useState<string | null>(null)

  useEffect(() => {
    const load = contractId ? ctGet(contractId).then((contract) => ({ contract })) : ctByLead(leadId)
    load.then(({ contract }) => {
      setCt(contract); setBook(contract.pricing); setSel(contract.selections)
      ctLink(contract.id).then((l) => setLink(l.url)).catch(() => {})
    }).catch(() => crmToast('Could not load the contract', 'error'))
  }, [leadId, contractId])

  const computed = useMemo(() => (book && sel ? ctCompute(book, sel) : null), [book, sel])
  if (!ct || !book || !sel || !computed) return <div className="p-6 text-[12px] text-txt-tertiary">Loading contract…</div>

  const locked = ct.locked
  const money = moneyIn(book.currency)
  const refresh = (c: Contract) => { setCt(c); setBook(c.pricing); setSel(c.selections) }
  const saveCustomer = (k: string, v: string) => {
    setCt({ ...ct, customer: { ...ct.customer, [k]: v } })
    ctPatch(ct.id, { customer: { [k]: v } }).catch(() => crmToast('Not saved', 'error'))
  }
  // Part-B fills (Lee, 7 Oct 2026): every slot must be completed before the
  // contract can be sent. Local mirror so the progress count updates live.
  const requiredFills = SOW_FILLS.filter((f) => !f.auto)
  const sowDone = requiredFills.filter((f) => String(ct.sowFills?.[String(f.i)] ?? '').trim() !== '').length
  // SOW switched off (Lee, 13 Oct 2026) → fills never gate sending.
  const hideSow = !!ct.options.hideSow
  const sowComplete = hideSow || sowDone === requiredFills.length
  // "Included in the price" line keys (Lee, 13 Oct 2026).
  const incKeys: string[] = (sel as any).includedKeys || []
  const toggleIncluded = (key: string) => saveSel({ includedKeys: incKeys.includes(key) ? incKeys.filter((k) => k !== key) : [...incKeys, key] } as any)
  const IncChk: React.FC<{ k: string }> = ({ k }) => (
    <label className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 cursor-pointer ml-2" title="Stays on the agreement with its quantity, but charges £0 — the price column reads Included">
      <input type="checkbox" checked={incKeys.includes(k)} disabled={locked} onChange={() => toggleIncluded(k)} className="w-3.5 h-3.5 accent-emerald-600" />
      Included in price
    </label>
  )
  const saveFill = (i: number, v: string) => {
    // FUNCTIONAL update (Lee, 7 Oct 2026 bugfix): the old spread captured a
    // stale `ct`, so rapid fills clobbered each other locally and the
    // Mark-as-sent button stayed greyed even with everything entered.
    setCt((prev) => (prev ? { ...prev, sowFills: { ...prev.sowFills, [String(i)]: v } } : prev))
    ctPatch(ct.id, { sowFills: { [String(i)]: v } }).catch(() => crmToast('Not saved', 'error'))
  }
  const saveSel = (p: Partial<CtSelections>) => {
    setSel({ ...sel, ...p } as CtSelections)
    ctPatch(ct.id, { selections: p }).catch((e: any) => crmToast(e?.message || 'Not saved', 'error'))
  }
  const saveOption = (k: string, v: any) => {
    setCt({ ...ct, options: { ...ct.options, [k]: v } as any })
    ctPatch(ct.id, { options: { [k]: v } as any }).catch(() => crmToast('Not saved', 'error'))
  }
  const saveProserv = (p: Record<string, any>) => {
    setCt({ ...ct, proserv: { ...ct.proserv, ...p } })
    ctPatch(ct.id, { proserv: p }).catch(() => crmToast('Not saved', 'error'))
  }
  const editBook = (fn: (b: CtPriceBook) => void) => {
    const b = JSON.parse(JSON.stringify(book)) as CtPriceBook
    fn(b); setBook(b); setPricingDirty(true)
  }
  const savePricing = () => ctPatch(ct.id, { pricing: book }).then(() => { setPricingDirty(false); crmToast('Pricing saved to this contract', 'success') }).catch((e: any) => crmToast(e?.message || 'Not saved', 'error'))

  const copy = (text: string, what: string) => navigator.clipboard?.writeText(text).then(() => crmToast(`${what} copied`, 'success'))
  const c = ct.customer || {}

  return (
    <div className="h-full flex flex-col bg-surface-secondary overflow-hidden">
      <CrmToaster />
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-3 bg-surface border-b border-border shrink-0 flex-wrap">
        <button onClick={() => navigate('/crm/contracts')} className="w-9 h-9 rounded-xl border border-border bg-surface flex items-center justify-center text-txt-secondary hover:text-txt-primary hover:bg-surface-hover shrink-0">‹</button>
        <Avatar name={c.companyName || ct.slug} size={36} />
        <div className="min-w-0">
          <p className="text-[16px] font-bold text-txt-primary truncate">{c.companyName || ct.slug}</p>
          <p className="text-[11px] text-txt-tertiary">Contract · {ct.slug}</p>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${CT_BADGE[ct.status]}`}>{ct.status.toUpperCase()}</span>
        {ct.viewedAt && <span className="text-[10px] text-txt-tertiary">viewed {new Date(ct.viewedAt).toLocaleDateString('en-GB')}</span>}
        {/* GO-LIVE / schedule start (Lee, 14 Oct 2026): drives the payment +
            renewal schedule on Monitoring. Editable at ANY status — go-live
            is usually only known after countersign. Blank = countersign date. */}
        <label className="inline-flex items-center gap-1.5 text-[10px] font-bold text-txt-tertiary uppercase" title="Payments and renewals on Monitoring run from this date. Leave blank to use the countersign date.">
          Go-live
          <input
            type="date"
            className="px-1.5 py-0.5 rounded border border-border bg-surface text-[11px] font-normal normal-case text-txt-primary"
            value={ct.startAt ? ct.startAt.slice(0, 10) : ''}
            onChange={(e) => {
              const v = e.target.value || null
              setCt({ ...ct, startAt: v })
              ctSetStart(ct.id, v).then(() => crmToast(v ? 'Go-live date saved — schedule runs from it' : 'Go-live cleared — schedule runs from countersign', 'success')).catch((err: any) => crmToast(err?.message || 'Could not save go-live date', 'error'))
            }}
          />
        </label>
        <span className="ml-auto flex items-center gap-2 flex-wrap">
          {ct.status === 'signed' && (
            <Button size="sm" onClick={() => {
              const name = window.prompt('Countersign as (your full name):')
              if (!name) return
              ctCountersign(ct.id, name).then(() => { crmToast('Countersigned — contract locked', 'success'); ctGet(ct.id).then((contract) => refresh(contract)) }).catch((e: any) => crmToast(e?.message || 'Could not countersign', 'error'))
            }}>Countersign & lock</Button>
          )}
          {ct.status === 'draft' && (
            <span className="inline-flex items-center gap-2" title={sowComplete ? undefined : 'Please complete Section B - Statement of Works Details before sending the contract'}>
              <Button
                size="sm" variant="secondary"
                disabled={!sowComplete}
                onClick={() => ctStatus(ct.id, 'sent').then(() => setCt((p) => (p ? { ...p, status: 'sent' } : p))).catch((e: any) => crmToast(e?.message || 'Complete Part B first', 'error'))}
              >Mark as sent</Button>
              {!sowComplete && (
                <span className="text-[10px] font-semibold text-amber-700 max-w-[260px] leading-tight">
                  Please complete Section B - Statement of Works Details before sending the contract ({sowDone}/{requiredFills.length})
                </span>
              )}
            </span>
          )}
          {ct.status === 'sent' && <Button size="sm" variant="secondary" onClick={() => ctStatus(ct.id, 'draft').then(() => setCt({ ...ct, status: 'draft' }))}>Back to draft</Button>}
          {!locked && ct.status !== 'void' && (
            <button onClick={() => { if (window.confirm('Void this contract? The customer link stops working.')) ctStatus(ct.id, 'void').then(() => setCt({ ...ct, status: 'void' })) }} className="text-[11px] font-semibold text-red-600 hover:underline">Void</button>
          )}
          <Button size="sm" variant="secondary" onClick={() => setLinkOpen(true)}>Customer link</Button>
          {link && <a href={link} target="_blank" rel="noreferrer" className="text-[12px] font-semibold text-brand-600 hover:underline whitespace-nowrap">Customer view →</a>}
          {link && (
            <a
              href={link.replace('/contract/', '/api/public/contract/').replace('?token=', '/pdf?token=')}
              target="_blank" rel="noreferrer"
              className="px-2 py-1 rounded-lg text-[11px] font-bold bg-surface-tertiary text-txt-primary border border-border hover:bg-surface-hover whitespace-nowrap no-underline"
              title="The branded PDF — the same file emailed on signing"
            >PDF preview</a>
          )}
        </span>
      </div>

      {locked && (
        <div className="px-5 py-2 bg-emerald-50 border-b border-emerald-200 text-[12px] font-semibold text-emerald-700 shrink-0">
          {ct.status === 'countersigned' ? 'Fully signed and locked.' : `Signed by ${ct.sign?.name || 'the customer'} ${ct.signedAt ? new Date(ct.signedAt).toLocaleString('en-GB') : ''} — countersign to lock.`} Changes are disabled.
        </div>
      )}

      <div className="flex-1 min-h-0 flex overflow-hidden">
        {/* ── BUILDER (left) ── */}
        <div className="w-[430px] shrink-0 overflow-y-auto p-4 space-y-3 border-r border-border">
          <div className={cardCls}>
            <p className="text-[13px] font-bold text-txt-primary mb-2">1 · Parties — the customer</p>
            <div className="grid grid-cols-2 gap-2">
              {([['companyName', 'Company legal name'], ['companyNumber', 'Company number'], ['registeredOffice', 'Registered office'], ['vatNumber', 'VAT number'], ['noticesEmail', 'Contact for notices (email)'], ['repName', 'Authorised representative'], ['repTitle', 'Representative title']] as const).map(([k, label]) => (
                <label key={k} className={`text-[10px] font-bold uppercase tracking-wide text-txt-tertiary ${k === 'registeredOffice' ? 'col-span-2' : ''}`}>
                  {label}
                  <input defaultValue={c[k] || ''} disabled={locked} onBlur={(e) => e.target.value !== (c[k] || '') && saveCustomer(k, e.target.value)} className={`${inputCls} w-full mt-0.5 font-normal normal-case tracking-normal`} />
                </label>
              ))}
            </div>
          </div>

          {/* 2 · CURRENCY & PRICES — the ONLY things we decide (Lee, 6 Oct
              2026). The customer picks plan, add-ons, Snagg users,
              integrations, training package, term and payment frequency on
              THEIR page; everything here is just what those choices cost. */}
          <div className={cardCls}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-[13px] font-bold text-txt-primary">2 · Currency &amp; prices</p>
              <div className="flex items-center gap-2">
                {pricingDirty && <Button size="sm" onClick={savePricing}>Save prices</Button>}
                <select value={book.currency} disabled={locked} onChange={(e) => editBook((b) => { b.currency = e.target.value })} className={`${inputCls} font-semibold`}>
                  <option value="GBP">GBP £</option>
                  <option value="USD">USD $</option>
                  <option value="EUR">EUR €</option>
                </select>
              </div>
            </div>
            <p className="text-[10px] text-txt-tertiary mt-0.5">This contract only — the central price book is untouched.</p>

            <SubCard title="Plans" hint="monthly">
              {Object.entries(book.packages).map(([k, p]) => (
                <PriceRow key={k} label={p.label} tbc={p.tbc} value={p.monthly} locked={locked} onChange={(n) => editBook((b) => { b.packages[k].monthly = n })} />
              ))}
            </SubCard>

            <SubCard title="Add-ons" hint="monthly">
              <PriceRow label={book.snagg.label} value={book.snagg.perUserMonthly} locked={locked} onChange={(n) => editBook((b) => { b.snagg.perUserMonthly = n })} />
              {Object.entries(book.modules).map(([k, m]) => (
                <PriceRow key={k} label={m.label} value={m.monthly} locked={locked} onChange={(n) => editBook((b) => { b.modules[k].monthly = n })} />
              ))}
            </SubCard>

            <SubCard title="Integrations" hint="monthly, each">
              {Object.entries(book.integrations).map(([k, m]) => (
                <PriceRow key={k} label={m.label} value={m.monthly} locked={locked} onChange={(n) => editBook((b) => { b.integrations[k].monthly = n })} />
              ))}
            </SubCard>

            <SubCard title="Training packages" hint="one-off — customer picks which">
              {Object.entries(book.proserv.training).map(([k, t]) => (
                <PriceRow key={k} label={t.label} tbc={t.tbc} value={t.fee} locked={locked} onChange={(n) => editBook((b) => { b.proserv.training[k].fee = n })} />
              ))}
            </SubCard>

            {/* Lee, 27 Oct 2026: hours-only at £195 (day rate retired — leave 0),
                + barcode scanners at £329 each, count per contract. */}
            <SubCard title="Professional services" hint="one-off, this contract">
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-wide text-txt-tertiary">Migration fee
                  <input type="number" defaultValue={ct.proserv?.migrationFee ?? 0} disabled={locked} onBlur={(e) => saveProserv({ migrationFee: Number(e.target.value) || 0 })} className={`${inputCls} w-full mt-0.5 font-normal`} />
                </label>
                <label className="text-[10px] font-bold uppercase tracking-wide text-txt-tertiary">Extra hour (£)
                  <input type="number" defaultValue={ct.proserv?.extraHour ?? book.proserv.extraHour} disabled={locked} onBlur={(e) => saveProserv({ extraHour: Number(e.target.value) || 0 })} className={`${inputCls} w-full mt-0.5 font-normal`} />
                </label>
                <label className="text-[10px] font-bold uppercase tracking-wide text-txt-tertiary">Extra day (£ · 0 = hours only)
                  <input type="number" defaultValue={ct.proserv?.extraDay ?? book.proserv.extraDay} disabled={locked} onBlur={(e) => saveProserv({ extraDay: Number(e.target.value) || 0 })} className={`${inputCls} w-full mt-0.5 font-normal`} />
                </label>
                <label className="text-[10px] font-bold uppercase tracking-wide text-txt-tertiary">Scanners × £ each
                  <span className="flex gap-1.5 mt-0.5">
                    <input type="number" min={0} defaultValue={ct.proserv?.scannerCount ?? 0} disabled={locked} onBlur={(e) => saveProserv({ scannerCount: Math.max(0, Number(e.target.value) || 0) })} className={`${inputCls} w-1/2 font-normal`} title="How many scanners" />
                    <input type="number" defaultValue={ct.proserv?.scannerEach ?? book.proserv.scannerEach ?? 329} disabled={locked} onBlur={(e) => saveProserv({ scannerEach: Number(e.target.value) || 0 })} className={`${inputCls} w-1/2 font-normal`} title="Price per scanner" />
                  </span>
                </label>
              </div>
            </SubCard>
          </div>

          {/* 3 · FIXED vs THEIR CHOICE (Lee, 6 Oct 2026): default = WE fix
              every section — we pick the value/tier and the price shown.
              Flipping a section to "they choose" opens it on their page.
              Training is ALWAYS our pick. */}
          <div className={cardCls}>
            <p className="text-[13px] font-bold text-txt-primary mb-1">3 · Fixed for them, or their choice</p>
            <p className="text-[10px] text-txt-tertiary mb-2">Default is fixed — the customer sees exactly what you set below. Flip a section to let them choose it on their page.</p>

            <FixRow label="Plan" choice={!!ct.options.choosePackage} locked={locked} onMode={(v) => saveOption('choosePackage', v)}>
              {!ct.options.choosePackage ? (
                <select value={sel.package} disabled={locked} onChange={(e) => saveSel({ package: e.target.value })} className={`${inputCls} w-full`}>
                  {Object.entries(book.packages).map(([k, p]) => <option key={k} value={k}>{p.label} — {money(p.monthly)}/mo</option>)}
                </select>
              ) : (
                <div className="flex flex-col gap-0.5">
                  <p className="text-[10px] text-txt-tertiary">Plans they can pick from:</p>
                  {Object.entries(book.packages).map(([k, p]) => {
                    const shown = !ct.options.shownPackages?.length || ct.options.shownPackages.includes(k)
                    return (
                      <label key={k} className="flex items-center gap-2 text-[12px] text-txt-primary cursor-pointer">
                        <input type="checkbox" checked={shown} disabled={locked} onChange={(e) => {
                          const all = Object.keys(book.packages)
                          const cur = ct.options.shownPackages?.length ? [...ct.options.shownPackages] : all
                          const next = e.target.checked ? [...new Set([...cur, k])] : cur.filter((x) => x !== k)
                          saveOption('shownPackages', next.length === all.length ? null : next)
                        }} className="w-4 h-4 accent-brand-600" />
                        {p.label} — {money(p.monthly)}/mo
                      </label>
                    )
                  })}
                </div>
              )}
            </FixRow>

            <FixRow label="Snagg users" choice={!!ct.options.chooseSnagg} locked={locked} onMode={(v) => saveOption('chooseSnagg', v)}>
              {!ct.options.chooseSnagg && <input type="number" min={0} value={sel.snaggUsers} disabled={locked} onChange={(e) => saveSel({ snaggUsers: Math.max(0, Number(e.target.value) || 0) })} className={`${inputCls} w-24`} />}
              {sel.snaggUsers > 0 && <IncChk k="snagg" />}
            </FixRow>

            <FixRow label="Modules (P&I)" choice={!!ct.options.chooseModules} locked={locked} onMode={(v) => saveOption('chooseModules', v)}>
              {!ct.options.chooseModules && Object.entries(book.modules).map(([k, m]) => (
                <label key={k} className="flex items-center gap-2 text-[12px] text-txt-primary cursor-pointer">
                  <input type="checkbox" checked={sel.modules.includes(k)} disabled={locked} onChange={(e) => saveSel({ modules: e.target.checked ? [...sel.modules, k] : sel.modules.filter((x) => x !== k) })} className="w-4 h-4 accent-brand-600" />
                  {m.label}
                  {sel.modules.includes(k) && <IncChk k={`module:${k}`} />}
                </label>
              ))}
            </FixRow>

            <FixRow label="Integrations" choice={!!ct.options.chooseIntegrations} locked={locked} onMode={(v) => saveOption('chooseIntegrations', v)}>
              {!ct.options.chooseIntegrations && (
                <div className="flex gap-1.5 flex-wrap">
                  {Object.entries(book.integrations).map(([k, m]) => {
                    const on = sel.integrations.includes(k)
                    return <button key={k} disabled={locked} onClick={() => saveSel({ integrations: on ? sel.integrations.filter((x) => x !== k) : [...sel.integrations, k] })} className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${on ? 'bg-brand-600 text-white border-brand-600' : 'bg-surface text-txt-secondary border-border'}`}>{m.label}</button>
                  })}
                </div>
              )}
              {sel.integrations.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-1">
                  {sel.integrations.map((k) => <IncChk key={k} k={`integration:${k}`} />)}
                </div>
              )}
            </FixRow>

            <FixRow label="Contract term" choice={!!ct.options.chooseTerm} locked={locked} onMode={(v) => saveOption('chooseTerm', v)}>
              {!ct.options.chooseTerm && (
                <select value={sel.termMonths} disabled={locked} onChange={(e) => saveSel({ termMonths: Number(e.target.value) as 12 | 24 | 36 })} className={`${inputCls} w-full`}>
                  {[12, 24, 36].map((t) => <option key={t} value={t}>{t} months</option>)}
                </select>
              )}
            </FixRow>

            <FixRow label="Payment frequency" choice={!!ct.options.chooseFrequency} locked={locked} onMode={(v) => saveOption('chooseFrequency', v)}>
              {!ct.options.chooseFrequency && (
                <select value={sel.frequency} disabled={locked} onChange={(e) => saveSel({ frequency: e.target.value as 'monthly' | 'annual' })} className={`${inputCls} w-full`}>
                  <option value="monthly">Monthly in advance</option>
                  <option value="annual">Annually in advance</option>
                </select>
              )}
            </FixRow>

            <div className="py-2.5 border-t border-border/70 mt-1">
              <div className="flex items-center gap-2">
                <span className="text-[12px] font-bold text-txt-primary flex-1">Implementation &amp; Training</span>
                <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-surface-tertiary text-txt-tertiary border border-border">ALWAYS OUR PICK</span>
              </div>
              <select value={sel.training} disabled={locked} onChange={(e) => saveSel({ training: e.target.value })} className={`${inputCls} w-full mt-1`}>
                {Object.entries(book.proserv.training).map(([k, t]) => <option key={k} value={k}>{t.label} — {t.fee ? money(t.fee) : 'included'}</option>)}
              </select>
              {!!book.proserv.training[sel.training]?.details?.length && (
                <ul className="mt-1 pl-4 list-disc text-[11px] text-txt-tertiary">
                  {book.proserv.training[sel.training].details!.map((d) => <li key={d}>{d}</li>)}
                </ul>
              )}
            </div>
          </div>

          {/* 4 · PART B — every amber fill slot of the Statement of Works
              (Lee, 7 Oct 2026). Sending is BLOCKED until all are filled. */}
          <div className={cardCls}>
            <div className="flex items-center gap-2 mb-1">
              <p className="text-[13px] font-bold text-txt-primary flex-1">4 · Statement of Works details (Part B)</p>
              {/* SWITCH OFF THE SOW (Lee, 13 Oct 2026): Part B disappears from
                  the page + PDF, Terms become Part B, fills stop gating Send. */}
              <button
                disabled={locked}
                onClick={() => saveOption('hideSow' as any, !hideSow)}
                className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${hideSow ? 'bg-surface-tertiary text-txt-secondary border-border' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}
                title={hideSow ? 'The SOW is OFF for this contract — click to include it again' : 'Click to switch the Statement of Works OFF for this contract'}
              >{hideSow ? 'SOW: OFF' : 'SOW: ON'}</button>
              {!hideSow && <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${sowComplete ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-300'}`}>{sowDone}/{requiredFills.length}{sowComplete ? ' ✓' : ''}</span>}
            </div>
            {hideSow && <p className="text-[10px] text-txt-tertiary mb-2">Switched off — this contract has no Statement of Works. The Terms become Part B and sending is not gated on these fields.</p>}
            {!hideSow && <p className="text-[10px] text-txt-tertiary mb-2">These complete the amber slots on the customer's agreement. The link can't be sent until every one is filled.</p>}
            {!hideSow && (() => {
              // GROUPED to mirror the document (Lee, 28 Oct 2026): each panel
              // maps to the Part-B section its answers fill, so the Studio
              // reads like the contract it produces.
              const GROUPS: Array<{ title: string; hint: string; idx: number[] }> = [
                { title: 'Legacy system & people', hint: 'SOW §1 header', idx: [0, 1, 2, 3] },
                { title: 'Data in scope', hint: 'the §1 scope table', idx: [4, 5, 6, 7, 8, 9, 10, 11, 12] },
                { title: 'Timeline & allowances', hint: '§1 steps · business days', idx: [13, 14, 15, 16, 18] },
                { title: 'Targets & billing', hint: 'sign-off · go-live · billing backstop', idx: [19, 20, 21] },
              ]
              const byI = new Map(requiredFills.map((f) => [f.i, f]))
              const renderFill = (f: (typeof requiredFills)[number]) => {
                const v = String(ct.sowFills?.[String(f.i)] ?? '')
                // Field type from the slot's placeholder: Yes/No → dropdown ·
                // date → date picker · counts → number.
                const kind = f.ph === 'Yes / No' ? 'yesno' : /^date$/i.test(f.ph) ? 'date' : /^\d+$|^x$/.test(f.ph) ? 'number' : 'text'
                const amber = !v ? 'border-amber-300 bg-amber-50/40' : ''
                const label = f.label.replace(/\s*\(Yes \/ No\)$/, '').replace(/^In scope — /, '')
                return (
                  <label key={f.i} className={`text-[10px] font-bold uppercase tracking-wide text-txt-tertiary ${kind === 'text' ? 'col-span-2' : ''}`}>
                    {label}
                    {kind === 'yesno' ? (
                      <select value={v} disabled={locked} onChange={(e) => saveFill(f.i, e.target.value)} className={`${inputCls} w-full mt-0.5 font-normal normal-case tracking-normal ${amber}`}>
                        <option value="">— choose —</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    ) : kind === 'date' ? (
                      <input type="date" value={v} disabled={locked} onChange={(e) => saveFill(f.i, e.target.value)} className={`${inputCls} w-full mt-0.5 font-normal normal-case tracking-normal ${amber}`} />
                    ) : kind === 'number' ? (
                      <input type="number" min={0} defaultValue={v} disabled={locked} onBlur={(e) => e.target.value !== v && saveFill(f.i, e.target.value)} className={`${inputCls} w-full mt-0.5 font-normal normal-case tracking-normal ${amber}`} />
                    ) : (
                      <input defaultValue={v} disabled={locked} placeholder={f.ph} onBlur={(e) => e.target.value !== v && saveFill(f.i, e.target.value)} className={`${inputCls} w-full mt-0.5 font-normal normal-case tracking-normal ${amber}`} />
                    )}
                  </label>
                )
              }
              return GROUPS.map((g) => {
                const fields = g.idx.map((i) => byI.get(i)).filter(Boolean) as typeof requiredFills
                const done = fields.filter((f) => String(ct.sowFills?.[String(f.i)] ?? '').trim() !== '').length
                return (
                  <SubCard key={g.title} title={`${g.title} ${done === fields.length ? '✓' : `(${done}/${fields.length})`}`} hint={g.hint}>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">{fields.map(renderFill)}</div>
                  </SubCard>
                )
              })
            })()}
          </div>
        </div>

        {/* ── LIVE PREVIEW (right) — what the customer page shows ── */}
        <div className="flex-1 min-w-0 overflow-y-auto p-5">
          <div className="max-w-[760px] mx-auto space-y-4">
            <div className={cardCls + ' text-center'}>
              <p className="text-[11px] font-bold tracking-widest text-brand-600">YOUR WEQUOTE AGREEMENT</p>
              <p className="text-[20px] font-bold text-txt-primary mt-1">Prepared for {c.companyName || '…'}</p>
              <p className="text-[12px] text-txt-tertiary mt-1">Sign → Migrate → Train → Go live · term starts the day you sign · price locked for the whole term · renews for 12 months unless 90 days' notice.</p>
            </div>

            <div className={cardCls}>
              <p className="text-[13px] font-bold text-txt-primary mb-2">Committed subscription</p>
              <table className="w-full text-[12px]">
                <thead><tr className="text-left text-[10px] uppercase tracking-wide text-txt-tertiary border-b border-border">
                  <th className="py-1.5">Item</th><th className="text-center">Qty</th><th className="text-right">Standard /mo</th><th className="text-center">Term discount</th><th className="text-right">Your price /mo</th>
                </tr></thead>
                <tbody>
                  {computed.lines.map((l) => (
                    <tr key={l.key} className="border-b border-border/60">
                      <td className="py-1.5 text-txt-primary">{l.label}</td>
                      <td className="text-center text-txt-secondary">{l.qty}</td>
                      <td className="text-right text-txt-secondary tabular-nums">{(l as any).included ? '—' : money(l.standardMonthly)}</td>
                      <td className="text-center text-txt-secondary">{(l as any).included ? '—' : l.discountPct ? `−${l.discountPct}%` : '—'}</td>
                      <td className="text-right font-semibold text-txt-primary tabular-nums">{(l as any).included ? 'Included' : money(l.yourMonthly)}</td>
                    </tr>
                  ))}
                  {computed.lines.length === 0 && <tr><td colSpan={5} className="py-2 text-txt-tertiary">Nothing selected yet.</td></tr>}
                </tbody>
                <tfoot><tr>
                  <td colSpan={4} className="py-2 text-[12px] font-bold text-txt-primary">Total Subscription Fees</td>
                  <td className="text-right tabular-nums py-2">
                    {sel.frequency === 'annual'
                      ? <><span className="text-[15px] font-bold text-txt-primary">{money(computed.billedAnnually)} /yr</span><span className="block text-[11px] font-semibold text-txt-tertiary">= {money(computed.monthlyTotal)} /mo</span></>
                      : <span className="text-[15px] font-bold text-txt-primary">{money(computed.monthlyTotal)} /mo</span>}
                  </td>
                </tr></tfoot>
              </table>
              <p className="text-[10px] text-txt-tertiary mt-1">
                {sel.termMonths}-month term · paid {sel.frequency === 'annual' ? `annually in advance (${money(computed.billedAnnually)} per contract year)` : 'monthly in advance'}
                {computed.savingPerMonth > 0 && <> · saving {money(computed.savingPerMonth)}/mo = <b>{money(computed.savingOverTerm)} over the term</b></>} · {book.vatNote}
              </p>
            </div>

            <div className={cardCls}>
              <p className="text-[13px] font-bold text-txt-primary mb-1">Term discounts — this basket, every option</p>
              <p className="text-[10px] text-txt-tertiary mb-2">The customer sees this exact table and their selection highlighted as they choose.</p>
              <table className="w-full text-[12px]">
                <thead><tr className="text-left text-[10px] uppercase tracking-wide text-txt-tertiary border-b border-border">
                  <th className="py-1.5">Contract term</th><th className="text-right">Paid monthly</th><th className="text-right">Paid annually in advance</th>
                </tr></thead>
                <tbody>
                  {computed.matrix.map((row) => (
                    <tr key={row.termMonths} className="border-b border-border/60">
                      <td className="py-1.5 font-semibold text-txt-primary">{row.termMonths} months</td>
                      {(['monthly', 'annual'] as const).map((f) => {
                        const cell = f === 'monthly' ? row.monthly : row.annual
                        const selCell = sel.termMonths === row.termMonths && sel.frequency === f
                        const saving = Math.round((computed.standardMonthly - cell.total) * row.termMonths * 100) / 100
                        return (
                          <td key={f} className={`text-right tabular-nums py-1.5 px-2 align-top ${selCell ? 'bg-brand-50 rounded-lg font-bold text-brand-700' : 'text-txt-secondary'}`}>
                            {f === 'monthly'
                              ? <span className="text-[13px] font-bold">{money(cell.total)}<span className="text-[10px]">/mo</span></span>
                              : <><span className="text-[13px] font-bold">{money((cell as any).perYear)}<span className="text-[10px]">/yr</span></span><span className="block text-[10px] text-txt-tertiary">= {money(cell.total)}/mo</span></>}
                            <span className={`block text-[10px] font-semibold ${saving > 0 ? 'text-emerald-600' : 'text-txt-tertiary'}`}>{saving > 0 ? `save ${money(saving)} over ${row.termMonths}mo (−${cell.pct}%)` : 'standard price'}</span>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={cardCls}>
              <p className="text-[13px] font-bold text-txt-primary mb-2">Professional services (one-off)</p>
              <table className="w-full text-[12px]">
                <tbody>
                  <tr className="border-b border-border/60"><td className="py-1.5 text-txt-primary">Data Migration & System Close-Down</td><td className="text-right text-txt-secondary">100% on signing</td><td className="text-right font-semibold tabular-nums">{money(Number(ct.proserv?.migrationFee || 0))}</td></tr>
                  {Number(ct.proserv?.scannerCount || 0) > 0 && <tr className="border-b border-border/60"><td className="py-1.5 text-txt-primary">Barcode scanners × {Number(ct.proserv?.scannerCount)}</td><td className="text-right text-txt-secondary">In full on signing</td><td className="text-right font-semibold tabular-nums">{money(Number(ct.proserv?.scannerCount) * Number(ct.proserv?.scannerEach ?? 329))}</td></tr>}
                  <tr className="border-b border-border/60"><td className="py-1.5 text-txt-primary">Implementation & Training — {computed.training.label} <span className="text-[10px] text-txt-tertiary">(customer's choice)</span></td><td className="text-right text-txt-secondary">In full on signing</td><td className="text-right font-semibold tabular-nums">{money(computed.training.fee)}</td></tr>
                  <tr><td className="py-1.5 text-txt-primary">Additional hours — Implementation & Training</td><td className="text-right text-txt-secondary">Paid in advance for set hours</td><td className="text-right text-txt-secondary tabular-nums">{Number(ct.proserv?.extraDay || 0) > 0 ? `${money(Number(ct.proserv?.extraDay))}/day · ` : ''}{money(Number(ct.proserv?.extraHour || 0))}/hr</td></tr>
                </tbody>
              </table>
            </div>

            <div className={cardCls}>
              <p className="text-[13px] font-bold text-txt-primary mb-1">Acceptance</p>
              {ct.sign ? (
                <p className="text-[12px] text-emerald-700 font-semibold">Signed by {ct.sign.name}{ct.sign.title ? `, ${ct.sign.title}` : ''} on {ct.signedAt ? new Date(ct.signedAt).toLocaleString('en-GB') : '—'}{ct.countersign ? ` · countersigned by ${ct.countersign.name}` : ' · awaiting countersign'}</p>
              ) : (
                <p className="text-[12px] text-txt-tertiary">Unsigned — the customer signs digitally at the bottom of their page (name, title, drawn signature; timestamp + IP recorded). You countersign here and the contract locks.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Customer link popup */}
      {linkOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setLinkOpen(false)}>
          <div className="bg-surface rounded-2xl border border-border shadow-xl p-5 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <p className="text-[15px] font-bold text-txt-primary mb-1">Customer contract link</p>
            <p className="text-[11px] text-txt-tertiary mb-3">Copy-link delivery only — send it over email/WhatsApp yourself. Rotating kills the old link instantly.</p>
            {/* SEND GATE (Lee, 7 Oct 2026): no copying until Part B is filled
                in — you can still open the Customer view yourself to check. */}
            {sowComplete ? (
              <div className="flex items-center gap-2">
                <input readOnly value={link || 'No link — rotate once'} className={`${inputCls} flex-1 text-[11px]`} />
                <Button size="sm" onClick={() => link && copy(link, 'Link')}>Copy</Button>
              </div>
            ) : (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-800">
                <b>Not ready to send.</b> Complete the Statement of Works fields first — {sowDone}/{requiredFills.length} done.
                You can still open <b>Customer view →</b> to preview what they'll see.
              </div>
            )}
            <div className="flex items-center justify-between mt-3">
              <button onClick={() => { if (window.confirm('Rotate the token? The old link stops working immediately.')) ctRotate(ct.id).then((r) => { setLink(r.url); crmToast('Token rotated', 'success') }) }} className="text-[11px] font-semibold text-red-600 hover:underline">Rotate token</button>
              <button onClick={() => setLinkOpen(false)} className="text-[12px] font-semibold text-txt-tertiary hover:text-txt-primary">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
